/**
 * LOBBERO STAYS — the host's guest guide (plan S7; lobbero_be/src/stays/routes/
 * guide.js, guide-media.js, guide-access.js; shapes from src/stays/guide/
 * serialize.js, limits from src/stays/guide/vocabulary.js).
 *
 * The staff guide carries NO access value: the access details travel only on
 * their own GET (stays:manage, no-store) and are never kept beyond the tab
 * that asked for them.
 */

export type GuideLanguage = 'en' | 'pt' | 'es';

/** GET/PUT /stays/listings/:id/guide → guide. Blank text is ''. */
export interface StaffGuide {
  portal_lead_hours: number;
  reveal_minutes: number;
  expiry_minutes: number;
  address_text: string;
  maps_url: string;
  directions: string;
  parking_space: string;
  parking_instructions: string;
  host_name: string;
  welcome_message: string;
  air_conditioning: string;
  heating: string;
  hot_water: string;
  rubbish: string;
  quiet_hours: string;
  checkout_instructions: string;
  emergency_phone: string;
  host_phone: string;
  checkout_checklist: string[];
  content_language: string | null;
  /** 0 = never saved. */
  version: number;
  updated_at: string | null;
}

/** The fields a guide PUT may carry (every one optional, `version` required). */
export type GuideContentField = Exclude<keyof StaffGuide, 'version' | 'updated_at'>;
export type GuideInput = Partial<Pick<StaffGuide, GuideContentField>> & { version: number };

export interface GuideFaq {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  sort_order: number;
}

export interface GuideFaqInput {
  question?: string;
  answer?: string;
  category?: string | null;
}

export type GuideTipKind = 'eat' | 'see' | 'nearby' | 'transport' | 'other';

export interface GuideTip {
  id: string;
  kind: GuideTipKind;
  name: string;
  note: string | null;
  address: string | null;
  maps_url: string | null;
  phone: string | null;
  media_id: string | null;
  sort_order: number;
}

export interface GuideTipInput {
  kind?: GuideTipKind;
  name?: string;
  note?: string | null;
  address?: string | null;
  maps_url?: string | null;
  phone?: string | null;
  media_id?: string | null;
}

export type GuideFixedSlot = 'cover' | 'host' | 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'step6';
export type GuideMediaSlot = GuideFixedSlot | 'gallery' | 'tip';

export interface GuideMedia {
  id: string;
  slot: GuideMediaSlot;
  title: string | null;
  category: string | null;
  sensitive: boolean;
  url: string;
  expires_in: number;
  content_type: string;
  width: number | null;
  height: number | null;
  sort_order: number;
}

export type GuideAccessKey =
  | 'building_code'
  | 'building_entry_steps'
  | 'floor'
  | 'apartment_door'
  | 'apartment_directions'
  | 'lockbox_code'
  | 'lockbox_instructions'
  | 'wifi_name'
  | 'wifi_password';

export const GUIDE_ACCESS_KEYS: ReadonlyArray<GuideAccessKey> = [
  'building_code',
  'building_entry_steps',
  'floor',
  'apartment_door',
  'apartment_directions',
  'lockbox_code',
  'lockbox_instructions',
  'wifi_name',
  'wifi_password',
];

/** Which keys are configured — booleans only, never a value. */
export type GuideAccessStatus = Partial<Record<GuideAccessKey, boolean>> & { updated_at: string | null };

export interface GuideLimits {
  faqs: number;
  tips: number;
  gallery: number;
  checklist: number;
}

/** GET /stays/listings/:id/guide */
export interface GuideBundle {
  guide: StaffGuide;
  faqs: GuideFaq[];
  tips: GuideTip[];
  media: GuideMedia[];
  access_status: GuideAccessStatus | null;
  limits: GuideLimits;
  defaults: { address: string; emergency_phone: string | null };
  listing: { check_in_time: string; check_out_time: string; country_code: string; timezone: string };
}

/** The nine values ('' = empty). Held only by the Access details tab. */
export type GuideAccessValues = Record<GuideAccessKey, string>;

/** GET /stays/listings/:id/access-details */
export interface GuideAccessDetails {
  access: GuideAccessValues;
  /** 0 = none saved yet. */
  version: number;
  updated_at: string | null;
  updated_by_name: string | null;
}

export type GuideAccessInput = Partial<GuideAccessValues> & { version: number };

export interface GuideAccessSaved {
  version: number;
  updated_at: string | null;
  access_status: GuideAccessStatus | null;
}

/** GET/PUT /stays/reservations/:id/guide-access (the stay's guide PIN). */
export interface StayGuideAccess {
  pin_required: boolean;
  /** Five digits, or null when the PIN is off. */
  pin: string | null;
  failed_count: number;
  locked_until: string | null;
  revealed: { count: number; first_at: string | null; last_at: string | null };
}
